import React from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Droplet,
  Gauge,
  Repeat2,
  Search,
  Zap,
} from 'lucide-react';
import { baseDeviationCode } from '../format';
import { FAULT_LABELS, namedFaults, stateOf } from '../status';

const ANOMALY_INDICATORS = [
  { code: 'DRY_RUN', label: 'Dry Run', Icon: Droplet },
  { code: 'LOW_PF', label: 'Low Power Factor', Icon: Gauge },
  { code: 'PHASE_CURRENT_IMBALANCE', label: 'Phase Current Imbalance', Icon: Zap },
  { code: 'VOLTAGE_IMBALANCE', label: 'Voltage Imbalance', Icon: Activity },
  { code: 'PHASE_LOSS', label: 'Phase Loss', Icon: Zap },
  { code: 'VOLTAGE_PHASE_LOSS', label: 'Voltage Phase Loss', Icon: Zap },
  { code: 'UNDER_VOLTAGE', label: 'Under Voltage', Icon: Activity },
  { code: 'OVER_VOLTAGE', label: 'Over Voltage', Icon: Activity },
  { code: 'MOTOR_OVERLOAD', label: 'Motor Overload', Icon: Gauge },
  { code: 'FLOW_RESTRICTION', label: 'Flow Restriction', Icon: Droplet },
  { code: 'SHORT_CYCLING', label: 'Short Cycling', Icon: Repeat2 },
];

const SUPPORT_INDICATORS = [
  { code: 'UNUSUAL_PUMP_BEHAVIOUR', label: 'Unusual Pump Behaviour', Icon: Search },
];

function indicatorsFor(payload) {
  const indicators = new Map(ANOMALY_INDICATORS.map((item) => [item.code, item]));
  const returnedFaults = namedFaults(payload);
  for (const fault of returnedFaults) {
    const code = String(fault.code || '').toUpperCase();
    if (!code || indicators.has(code)) continue;
    indicators.set(code, {
      code,
      label: fault.label || FAULT_LABELS[code] || code.replaceAll('_', ' '),
      Icon: Activity,
    });
  }
  return [...indicators.values(), ...SUPPORT_INDICATORS];
}

function warningRisk(code, payload) {
  return (payload.early_warnings || []).some((warning) => {
    const warningCode = baseDeviationCode(warning);
    if (code === 'LOW_PF') return warningCode.startsWith('PF_');
    if (code === 'PHASE_CURRENT_IMBALANCE') return warningCode.includes('CURRENT_IMBALANCE');
    if (code === 'VOLTAGE_IMBALANCE') return warningCode.includes('VOLTAGE_IMBALANCE');
    if (code === 'DRY_RUN') return warningCode.startsWith('FLOW') || warningCode.startsWith('TOTAL_KW');
    if (code === 'PHASE_LOSS') return warningCode.includes('CURRENT') && warningCode.includes('LOW');
    if (code === 'VOLTAGE_PHASE_LOSS' || code === 'UNDER_VOLTAGE') return warningCode.includes('VOLTAGE') && warningCode.includes('LOW');
    if (code === 'OVER_VOLTAGE') return warningCode.includes('VOLTAGE') && warningCode.includes('HIGH');
    if (code === 'MOTOR_OVERLOAD') return warningCode.includes('CURRENT') && warningCode.includes('HIGH');
    if (code === 'FLOW_RESTRICTION') return warningCode.startsWith('FLOW') || warningCode.startsWith('TOTAL_KW');
    return false;
  });
}

function statusFor(code, payload) {
  const state = stateOf(payload);
  const activeCodes = namedFaults(payload).map((fault) => fault.code);
  const genericActive = state === 'ANOMALY' && activeCodes.length === 0;

  if (code === 'UNUSUAL_PUMP_BEHAVIOUR') {
    return genericActive
      ? { tone: 'red', text: 'Active issue', Icon: AlertCircle }
      : { tone: 'grey', text: 'No active issue', Icon: Clock3 };
  }
  if (state === 'ANOMALY' && activeCodes.includes(code)) return { tone: 'red', text: 'Active issue', Icon: AlertCircle };
  if (warningRisk(code, payload)) {
    return { tone: 'amber', text: 'Risk developing', Icon: AlertTriangle };
  }
  if (['NORMAL', 'ANOMALY'].includes(state)) return { tone: 'green', text: 'Normal', Icon: CheckCircle2 };
  return { tone: 'grey', text: state === 'OFF' ? 'Pump off' : 'Learning', Icon: Clock3 };
}

export function PumpHealthIndicators({ payload }) {
  if (!payload) return null;
  const indicators = indicatorsFor(payload);

  return (
    <section className="card-panel health-panel" aria-labelledby="health-title">
      <div className="panel-heading">
        <div>
          <span className="panel-kicker">Condition checks</span>
          <h2 id="health-title">Pump Health Indicators</h2>
        </div>
        <span className="panel-meta">{indicators.length} indicators</span>
      </div>
      <div className="health-grid">
        {indicators.map(({ code, label, Icon }) => {
          const status = statusFor(code, payload);
          const StatusIcon = status.Icon;
          return (
            <article className={`health-card is-${status.tone}`} key={code}>
              <span className="health-icon" aria-hidden="true">
                <Icon size={18} />
              </span>
              <div>
                <strong>{label}</strong>
                <span className={`indicator-status is-${status.tone}`}>
                  <StatusIcon size={13} />
                  {status.text}
                </span>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default PumpHealthIndicators;
